import {
  AutomationActionModel,
  AutomationConditionModel,
  AutomationJobTaskConditionModel,
  AutomationUserModel,
  GetAutomationsResponse,
  JobTaskAutomationModel,
  WorkAutomationModel,
} from "../../../../slices/AutomationSlice";
import {
  AutomationItem,
  AutomationLookupOptions,
} from "./automation.constants";
import {
  parseAutomationActionValue,
  parseAutomationConditionValue,
  resolveOptionLabel,
} from "./automation.helpers";

type AutomationMaps = {
  workConditionById: Map<string, AutomationConditionModel>;
  workActionById: Map<string, AutomationActionModel>;
  jobTaskConditionById: Map<string, AutomationJobTaskConditionModel>;
  jobTaskActionById: Map<string, AutomationActionModel>;
};

type MapAutomationsParams = {
  data?: GetAutomationsResponse;
  workName: string;
  options: AutomationLookupOptions;
  workConditions: AutomationConditionModel[];
  jobTaskConditions: AutomationJobTaskConditionModel[];
};

const getLastRunStatus = (
  automation: WorkAutomationModel | JobTaskAutomationModel,
): AutomationItem["lastRunStatus"] => {
  if (!automation.isExecuted) {
    return "idle";
  }

  return automation.isSuccess ? "success" : "failed";
};

const getAutomationUserName = (user?: AutomationUserModel | null) => {
  const fullName = [
    user?.userProfile?.firstName,
    user?.userProfile?.middleName,
    user?.userProfile?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || user?.userProfile?.email || "";
};

const createAutomationMaps = (
  workConditions: AutomationConditionModel[],
  jobTaskConditions: AutomationJobTaskConditionModel[],
): AutomationMaps => {
  const workConditionById = new Map<string, AutomationConditionModel>();
  const workActionById = new Map<string, AutomationActionModel>();
  const jobTaskConditionById = new Map<
    string,
    AutomationJobTaskConditionModel
  >();
  const jobTaskActionById = new Map<string, AutomationActionModel>();

  workConditions.forEach((condition) => {
    workConditionById.set(condition.id, condition);
    condition.conditionAction.forEach(({ action }) => {
      workActionById.set(action.id, action);
    });
  });

  jobTaskConditions.forEach((condition) => {
    jobTaskConditionById.set(condition.id, condition);
    condition.jobTaskConditionAction.forEach(({ action }) => {
      jobTaskActionById.set(action.id, action);
    });
  });

  return {
    workConditionById,
    workActionById,
    jobTaskConditionById,
    jobTaskActionById,
  };
};

export const mapAutomationsResponseToItems = ({
  data,
  workName,
  options,
  workConditions,
  jobTaskConditions,
}: MapAutomationsParams): AutomationItem[] => {
  const automationMaps = createAutomationMaps(
    workConditions,
    jobTaskConditions,
  );
  const getCreatedByLabel = (createdById: string) => {
    return resolveOptionLabel(options, "users", createdById, "Unknown user");
  };

  const workItems: AutomationItem[] =
    data?.workAutomations.map((automation) => {
      const condition =
        automation.condition ??
        automationMaps.workConditionById.get(automation.conditionId);
      const action =
        automation.action ??
        automationMaps.workActionById.get(automation.actionId);

      return {
        id: automation.id,
        scope: "work" as const,
        workItemId: automation.workId,
        workItemTitle: automation.work?.name || workName,
        conditionId: automation.conditionId,
        conditionKey: condition?.conditionKey,
        conditionValue: parseAutomationConditionValue(
          automation.conditionValue,
        ),
        actionId: automation.actionId,
        actionKey: action?.actionKey,
        actionValue: parseAutomationActionValue(automation.actionValue),
        createdBy:
          getAutomationUserName(automation.createdBy) ||
          getCreatedByLabel(automation.createdById),
        // Copies made from the work type have no creator (QA batch 14).
        sourceText:
          automation.createdById || automation.createdBy
            ? undefined
            : "Added from the work type template",
        createdAt: automation.createdAt,
        lastRunAt: automation.executedOn ?? undefined,
        lastRunStatus: getLastRunStatus(automation),
      };
    }) ?? [];

  const jobTaskItems: AutomationItem[] =
    data?.workTaskAutomations.map((automation) => {
      const condition =
        automation.condition ??
        automationMaps.jobTaskConditionById.get(automation.conditionId);
      const action =
        automation.action ??
        automationMaps.jobTaskActionById.get(automation.actionId);
      const workTask = automation.workTask ?? automation.jobTask ?? null;

      return {
        id: automation.id,
        scope: "subtask" as const,
        subtaskId: automation.jobTaskId,
        subtaskTitle: workTask?.name || "Selected subtask",
        conditionId: automation.conditionId,
        conditionKey: condition?.conditionKey,
        conditionValue: parseAutomationConditionValue(
          automation.conditionValue,
        ),
        actionId: automation.actionId,
        actionKey: action?.actionKey,
        actionValue: parseAutomationActionValue(automation.actionValue),
        createdBy:
          getAutomationUserName(automation.createdBy) ||
          getCreatedByLabel(automation.createdById),
        // Copies made from the subtask template have no creator (QA batch 14).
        sourceText:
          automation.createdById || automation.createdBy
            ? undefined
            : "Added from the subtask template",
        createdAt: automation.createdAt,
        lastRunAt: automation.executedOn ?? undefined,
        lastRunStatus: getLastRunStatus(automation),
      };
    }) ?? [];

  return [...workItems, ...jobTaskItems].sort(
    (first, second) =>
      new Date(second.createdAt).getTime() -
      new Date(first.createdAt).getTime(),
  );
};
